import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import ReactTooltip from "react-tooltip";
import "./serviceLifeChangeConfirmationModal.css";

const ACCENT = "#1a73e8";
const TOOLTIP_ID = "service-life-change-tip";

const isEmpty = value => value === "" || value === null || value === undefined || value === "null";
const orNull = value => (isEmpty(value) ? "null" : value);

const lifecycleRank = field => {
    const name = String(field ?? "");
    if (name.endsWith("usefull_life_remaining")) return 1;
    if (name.endsWith("end_of_service_life")) return 2;
    if (name.endsWith("service_life")) return 0;
    return -1;
};
const isLifecycleField = field => lifecycleRank(field) >= 0;

const Row = ({ checked, disabled, onToggle, label, badge, from, to, isFirst, tooltip, hideFrom }) => {
    const sentence = hideFrom ? `${label} will be updated to "${orNull(to)}"` : `${label} will change from "${orNull(from)}" to "${orNull(to)}"`;
    const sentenceRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useLayoutEffect(() => {
        const element = sentenceRef.current;
        setIsTruncated(!!element && element.scrollWidth > element.clientWidth);
    }, [sentence]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [isTruncated]);

    return (
        <label className={`container-check not-draggable mb-0 slc-row${isFirst ? " slc-row-first" : ""}${disabled ? " slc-row-disabled" : ""}`}>
            <input type="checkbox" checked={checked} disabled={disabled} onChange={() => !disabled && onToggle()} />
            <span className="checkmark slc-check" data-tip={tooltip || undefined} data-for={TOOLTIP_ID}></span>
            {badge ? (
                <>
                    <span className="slc-label">{label}</span>
                    <span className="slc-badge">{badge}</span>
                </>
            ) : (
                <span className="slc-sentence" ref={sentenceRef} data-tip={isTruncated ? sentence : undefined} data-for={TOOLTIP_ID}>
                    {label} <span className="slc-change-to">{hideFrom ? "will be updated" : "will change"}</span>{" "}
                    {hideFrom ? null : (
                        <>
                            <span className="slc-change-to">from</span> <span className="slc-from">"{orNull(from)}"</span>{" "}
                        </>
                    )}
                    <span className="slc-change-to">to</span> <span className="slc-to">"{orNull(to)}"</span>
                </span>
            )}
        </label>
    );
};

const ServiceLifeChangeConfirmationModal = ({
    sourceLabel = "Uniformat",
    entityLabel = "Asset",
    sourceFrom,
    sourceTo,
    isMultiEdit = false,
    rows = [],
    onApply,
    onCancel
}) => {
    const selectableRows = useMemo(() => rows.filter(row => !row?.derived), [rows]);
    const orderedRows = useMemo(() => {
        const lifecycle = rows.filter(row => isLifecycleField(row?.field)).sort((a, b) => lifecycleRank(a?.field) - lifecycleRank(b?.field));
        return [...lifecycle, ...rows.filter(row => !isLifecycleField(row?.field))];
    }, [rows]);
    const lifecycleFields = useMemo(() => orderedRows.filter(row => isLifecycleField(row?.field)).map(row => row?.field), [orderedRows]);
    const [selected, setSelected] = useState([]);

    const toggle = field => {
        const group = isLifecycleField(field) ? lifecycleFields : [field];
        setSelected(prev => (prev.includes(field) ? prev.filter(item => !group.includes(item)) : [...new Set([...prev, ...group])]));
    };

    const allSelected = selectableRows.length > 0 && selectableRows.every(row => selected.includes(row?.field));
    const toggleAll = () => setSelected(allSelected ? [] : orderedRows.map(row => row?.field));

    const changedLabels = selectableRows.map(row => row?.label).join(", ");
    const serviceLifeRow = selectableRows.find(row => lifecycleRank(row?.field) === 0);
    const hasOnlyEmptyValues = selectableRows.length > 0 && selectableRows.every(row => isEmpty(row?.to));
    const currentServiceLife = isMultiEdit ? undefined : serviceLifeRow?.from;

    const isSingleServiceLife = serviceLifeRow && selectableRows.length === 1;

    const heading = hasOnlyEmptyValues
        ? `You have selected a new "${sourceLabel}" which has NO ${changedLabels} definition`
        : isSingleServiceLife
          ? `You have selected a new "${sourceLabel}" which has a DIFFERENT Service Life definition [${serviceLifeRow?.to}]${
                isEmpty(currentServiceLife) ? "" : ` than the current ${entityLabel} value [${currentServiceLife}]`
            }.`
          : `You have selected a new "${sourceLabel}" which has DIFFERENT ${changedLabels} definitions.`;

    return (
        <div
            id="modalId"
            className="modal modal-region deleteModal slc-modal"
            style={{ display: "block" }}
            role="dialog"
            aria-labelledby="exampleModalLabel"
            aria-hidden="true"
        >
            <Draggable cancel=".not-draggable">
                <div className="modal-dialog draggable slc-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button type="button" className="close not-draggable" data-dismiss="modal" aria-label="Close" onClick={() => onCancel()}>
                                <span aria-hidden="true">
                                    <img src="/img/close.svg" alt="" />
                                </span>
                            </button>
                        </div>
                        <div className="modal-body region-otr slc-body">
                            <div className="not-draggable slc-heading-row">
                                <span className="slc-exclaim">!</span>
                                <h2 className="slc-heading">{heading}</h2>
                            </div>

                            <p className="not-draggable slc-list-heading">Select the fields to update</p>
                            <div className="not-draggable slc-list">
                                <label className="container-check mb-0 slc-row slc-row-first slc-select-all">
                                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                                    <span className="checkmark slc-check"></span>
                                    <span className="slc-select-all-label">Select All</span>
                                </label>
                                <Row
                                    checked
                                    disabled
                                    label={sourceLabel}
                                    hideFrom={isMultiEdit}
                                    {...(sourceFrom === undefined && sourceTo === undefined
                                        ? { badge: "Always updated" }
                                        : { from: sourceFrom, to: sourceTo })}
                                />
                                {orderedRows.map(row => (
                                    <Row
                                        key={row?.field}
                                        checked={selected.includes(row?.field)}
                                        label={row?.label}
                                        from={row?.from}
                                        to={row?.to}
                                        hideFrom={isMultiEdit}
                                        onToggle={() => toggle(row?.field)}
                                    />
                                ))}
                            </div>
                            <ReactTooltip
                                id={TOOLTIP_ID}
                                effect="solid"
                                place="top"
                                event="mouseenter"
                                eventOff="mouseleave"
                                backgroundColor={ACCENT}
                            />
                            <div className="not-draggable slc-note">
                                <div>
                                    <span className="slc-note-term">Apply</span> &ndash; Updates {sourceLabel} along with the checked fields.
                                </div>
                                <div>
                                    <span className="slc-note-term">Cancel</span> &ndash; Discards the change, {sourceLabel} is not updated.
                                </div>
                            </div>

                            <div className="not-draggable slc-buttons">
                                <button type="button" className="slc-cancel-btn" onClick={() => onCancel()}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="slc-apply-btn"
                                    onClick={() => onApply(selectableRows.filter(row => selected.includes(row?.field)).map(row => row?.field))}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Draggable>
        </div>
    );
};

export default ServiceLifeChangeConfirmationModal;
